import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Cesium from 'cesium';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { OpenSkyCentralService, OpenSkyFlightInfo } from './opensky-central.service';

export type { OpenSkyFlightInfo } from './opensky-central.service';

@Injectable({
  providedIn: 'root'
})
export class FlightRadarGlobeService {
  private viewer: Cesium.Viewer | null = null;
  private openSkyEntitiesMap = new Map<string, Cesium.Entity>();
  private selectedFlight: OpenSkyFlightInfo | null = null;
  private selectedEntity: Cesium.Entity | null = null;
  private isFullDetailsOpen = false;
  private popupOverlayElement: HTMLElement | null = null;
  private removePostRenderListener: (() => void) | null = null;
  private planeClickHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private centralSub: Subscription | null = null;

  // Reactive State
  private isLayerVisibleSubject = new BehaviorSubject<boolean>(false);
  public isLayerVisible$: Observable<boolean> = this.isLayerVisibleSubject.asObservable();

  private trackedPlaneCountSubject = new BehaviorSubject<number>(0);
  public trackedPlaneCount$: Observable<number> = this.trackedPlaneCountSubject.asObservable();

  private totalApiFlightCountSubject = new BehaviorSubject<number>(0);
  public totalApiFlightCount$: Observable<number> = this.totalApiFlightCountSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private openSkyCentralService: OpenSkyCentralService
  ) {}

  public isLayerVisible(): boolean {
    return this.isLayerVisibleSubject.getValue();
  }

  public get isLoading(): boolean {
    return this.isLoadingSubject.getValue();
  }

  public init(viewer: Cesium.Viewer): void {
    this.viewer = viewer;
    // Layer is OFF by default when app loads (No API call until user clicks the button)
    this.isLayerVisibleSubject.next(false);
    this.isLoadingSubject.next(false);
  }

  public async toggleLayer(visible?: boolean): Promise<boolean> {
    const targetState = visible !== undefined ? visible : !this.isLayerVisibleSubject.getValue();
    if (!this.viewer) {
      return false;
    }

    if (targetState) {
      // Turn ON Flight Radar layer
      const success = await this.startFlightRadar(this.viewer);
      return success;
    } else {
      // Turn OFF Flight Radar layer
      this.stopFlightRadar();
      return true;
    }
  }

  public async startFlightRadar(viewer: Cesium.Viewer): Promise<boolean> {
    if (!viewer) return false;
    this.viewer = viewer;
    this.isLayerVisibleSubject.next(true);

    this.clearEntitiesFromGlobe(viewer);
    this.isLoadingSubject.next(true);

    // Ensure central service is running
    this.openSkyCentralService.startCentralService();

    // Continuous rendering setup
    viewer.clock.shouldAnimate = true;
    viewer.clock.multiplier = 1.0;
    if (viewer.scene) {
      viewer.scene.requestRenderMode = false;
    }

    // Unlock camera tracking
    viewer.trackedEntity = undefined;
    if (viewer.camera) {
      viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
    }

    // Subscribe to centralized active aircraft pool
    if (this.centralSub) {
      this.centralSub.unsubscribe();
    }
    this.centralSub = this.openSkyCentralService.activeAircraft$.subscribe((aircraftList) => {
      if (this.isLayerVisibleSubject.getValue() && this.viewer) {
        this.trackedPlaneCountSubject.next(aircraftList.length);
        this.totalApiFlightCountSubject.next(this.openSkyCentralService.getTotalApiCount());
        this.renderFlightEntities(this.viewer, aircraftList);
        this.isLoadingSubject.next(false);
      }
    });

    // Initial render from current cached active pool
    const initialList = this.openSkyCentralService.getActiveAircraft();
    if (initialList.length > 0) {
      this.trackedPlaneCountSubject.next(initialList.length);
      this.totalApiFlightCountSubject.next(this.openSkyCentralService.getTotalApiCount());
      this.renderFlightEntities(viewer, initialList);
      this.isLoadingSubject.next(false);
    } else {
      // Trigger instant sync attempt
      this.openSkyCentralService.fetchOpenSkyData().then(() => {
        this.isLoadingSubject.next(false);
      });
    }

    this.setupPlaneClickListener(viewer);
    this.setupCameraOcclusionListener(viewer);
    return true;
  }

  public clearEntitiesFromGlobe(viewer?: Cesium.Viewer): void {
    const v = viewer || this.viewer;

    this.openSkyEntitiesMap.forEach((entity) => {
      entity.show = false;
      if (v && v.entities) {
        v.entities.remove(entity);
      }
    });
    this.openSkyEntitiesMap.clear();

    if (v && v.entities && v.entities.values) {
      const toRemove: Cesium.Entity[] = [];
      v.entities.values.forEach((ent: any) => {
        if (ent && (ent.openSkyData || (ent.name && typeof ent.name === 'string' && ent.name.startsWith('OpenSky Flight')))) {
          toRemove.push(ent);
        }
      });
      toRemove.forEach((ent) => {
        ent.show = false;
        v.entities.remove(ent);
      });
    }

    if (v) {
      this.removePopupOverlay(v);
    }
    this.selectedFlight = null;
  }

  private removeOcclusionListener: (() => void) | null = null;

  private setupCameraOcclusionListener(viewer: Cesium.Viewer): void {
    this.removeCameraOcclusionListener();

    if (!viewer || !viewer.scene) return;

    this.removeOcclusionListener = viewer.scene.postRender.addEventListener(() => {
      if (!this.isLayerVisibleSubject.getValue() || !viewer || !viewer.scene || !viewer.scene.camera || !viewer.scene.globe) {
        return;
      }

      const camera = viewer.scene.camera;
      const globe = viewer.scene.globe;
      const occluder = new Cesium.Occluder(
        Cesium.BoundingSphere.fromEllipsoid(globe.ellipsoid),
        camera.position
      );

      const currentTime = viewer.clock.currentTime;

      this.openSkyEntitiesMap.forEach((entity) => {
        if (!entity || !entity.position) return;

        const cartesianPos = (entity.position as any).getValue(currentTime);
        if (!cartesianPos) {
          entity.show = false;
          return;
        }

        const isVisibleOnFront = occluder.isPointVisible(cartesianPos);
        entity.show = isVisibleOnFront;
      });
    });
  }

  private removeCameraOcclusionListener(): void {
    if (this.removeOcclusionListener) {
      this.removeOcclusionListener();
      this.removeOcclusionListener = null;
    }
  }

  public stopFlightRadar(): void {
    if (this.centralSub) {
      this.centralSub.unsubscribe();
      this.centralSub = null;
    }

    this.openSkyCentralService.stopCentralService();

    this.removeCameraOcclusionListener();
    this.isLayerVisibleSubject.next(false);
    this.clearEntitiesFromGlobe(this.viewer || undefined);
    this.removePlaneClickListener();
    this.selectedFlight = null;
    this.isLoadingSubject.next(false);
    this.trackedPlaneCountSubject.next(0);
    this.totalApiFlightCountSubject.next(0);
  }

  public flyToNigeria(): void {
    if (!this.viewer) return;
    // Smooth camera view centered directly over Nigeria (Lat 9.0° N, Lon 7.5° E) at 2.5M meters height
    this.viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(7.5, 9.0, 2500000),
      orientation: {
        heading: Cesium.Math.toRadians(0.0),
        pitch: Cesium.Math.toRadians(-90.0),
        roll: 0.0
      },
      duration: 2.5
    });
  }

  public flyToGlobalView(): void {
    if (!this.viewer) return;
    // High altitude top-down view showing global airspace centered over West Africa / Nigeria
    this.viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(8.0, 10.0, 14000000),
      orientation: {
        heading: Cesium.Math.toRadians(0.0),
        pitch: Cesium.Math.toRadians(-90.0),
        roll: 0.0
      },
      duration: 2.5
    });
  }

  public async fetchOpenSkyLiveFlights(): Promise<OpenSkyFlightInfo[]> {
    return this.openSkyCentralService.getActiveAircraft();
  }

  private getAirplaneSvgDataUri(): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><path fill="#00d2ff" stroke="#ffffff" stroke-width="2" d="M32 4 L37 20 L58 30 L58 36 L37 30 L37 48 L44 54 L44 58 L32 54 L20 58 L20 54 L27 48 L27 30 L6 36 L6 30 L27 20 Z"/></svg>`;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  public renderFlightEntities(viewer: Cesium.Viewer, flights: OpenSkyFlightInfo[]): void {
    if (!viewer || !this.isLayerVisibleSubject.getValue()) {
      return;
    }

    const currentIcaoSet = new Set<string>();
    const nowTime = Cesium.JulianDate.now();
    const svgAirplaneUri = this.getAirplaneSvgDataUri();

    flights.forEach((f) => {
      if (
        typeof f.latitude !== 'number' ||
        typeof f.longitude !== 'number' ||
        isNaN(f.latitude) ||
        isNaN(f.longitude) ||
        f.latitude < -90 ||
        f.latitude > 90 ||
        f.longitude < -180 ||
        f.longitude > 180
      ) {
        return;
      }

      currentIcaoSet.add(f.icao24);

      const pos = Cesium.Cartesian3.fromDegrees(f.longitude, f.latitude, Math.max(f.altitudeMeters, 1000));

      const speedMps = Math.max((f.velocityKnots || 200) * 0.514444, 100);
      const headingRad = (f.headingDeg || 0) * Math.PI / 180.0;

      const tPast = Cesium.JulianDate.addSeconds(nowTime, -5.0, new Cesium.JulianDate());
      const pastLat = f.latitude - (speedMps * 5.0 * Math.cos(headingRad)) / 111139.0;
      const pastLon = f.longitude - (speedMps * 5.0 * Math.sin(headingRad)) / (111139.0 * Math.max(Math.cos(f.latitude * Math.PI / 180.0), 0.1));
      const posPast = Cesium.Cartesian3.fromDegrees(pastLon, pastLat, Math.max(f.altitudeMeters, 1000));

      let entity = this.openSkyEntitiesMap.get(f.icao24);

      if (!entity) {
        const positionProperty = new Cesium.SampledPositionProperty();
        positionProperty.setInterpolationOptions({
          interpolationDegree: 1,
          interpolationAlgorithm: Cesium.LinearApproximation
        });
        positionProperty.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        positionProperty.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;

        positionProperty.addSample(tPast, posPast);
        positionProperty.addSample(nowTime, pos);

        const rotationCallback = new Cesium.CallbackProperty(() => {
          const flightData = (entity as any)?.openSkyData;
          const heading = flightData && typeof flightData.headingDeg === 'number' ? flightData.headingDeg : f.headingDeg;
          return Cesium.Math.toRadians(-heading);
        }, false);

        const isSelected = this.selectedFlight?.icao24 === f.icao24;

        entity = viewer.entities.add({
          name: `OpenSky Flight ${f.callsign}`,
          position: positionProperty,
          billboard: {
            image: svgAirplaneUri,
            rotation: rotationCallback,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            width: 34,
            height: 34
          },
          path: {
            show: isSelected,
            resolution: 1,
            leadTime: 0,
            trailTime: 1800,
            material: new Cesium.PolylineGlowMaterialProperty({
              glowPower: 0.35,
              color: Cesium.Color.fromCssColorString('#00d2ff')
            }),
            width: 4
          }
        });
        (entity as any).openSkyData = f;
        this.openSkyEntitiesMap.set(f.icao24, entity);
      } else {
        const posProp = entity.position as Cesium.SampledPositionProperty;
        if (posProp && posProp.getValue) {
          const currentPos = posProp.getValue(nowTime);
          const distMeters = currentPos ? Cesium.Cartesian3.distance(currentPos, pos) : 0;
          if (distMeters > 15000) {
            const newPosProp = new Cesium.SampledPositionProperty();
            newPosProp.setInterpolationOptions({
              interpolationDegree: 1,
              interpolationAlgorithm: Cesium.LinearApproximation
            });
            newPosProp.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
            newPosProp.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
            newPosProp.addSample(tPast, posPast);
            newPosProp.addSample(nowTime, pos);
            entity.position = newPosProp as any;
          } else {
            posProp.addSample(nowTime, pos);
          }
        } else {
          (entity.position as any).setValue(pos);
        }
        (entity as any).openSkyData = f;

        const isSelected = this.selectedFlight?.icao24 === f.icao24;
        if (entity.path) {
          entity.path.show = new Cesium.ConstantProperty(isSelected);
        }
      }
    });

    // Clean up inactive plane entities
    this.openSkyEntitiesMap.forEach((entity, icao24) => {
      if (!currentIcaoSet.has(icao24)) {
        viewer.entities.remove(entity);
        this.openSkyEntitiesMap.delete(icao24);
      }
    });

    if (this.selectedFlight && this.openSkyEntitiesMap.has(this.selectedFlight.icao24)) {
      const updatedEntity = this.openSkyEntitiesMap.get(this.selectedFlight.icao24);
      if (updatedEntity && (updatedEntity as any).openSkyData) {
        this.selectedFlight = (updatedEntity as any).openSkyData;
        this.selectedEntity = updatedEntity;
        this.createOrUpdateOverlay(viewer, this.selectedFlight!, updatedEntity);
      }
    }

    if (viewer.scene) {
      viewer.scene.requestRender();
    }
  }

  public selectFlight(viewer: Cesium.Viewer, flightData: OpenSkyFlightInfo, targetEntity: Cesium.Entity, openFullDetails = false): void {
    this.selectedFlight = flightData;
    this.selectedEntity = targetEntity;
    this.isFullDetailsOpen = openFullDetails;

    if (this.removePostRenderListener) {
      this.removePostRenderListener();
      this.removePostRenderListener = null;
    }

    this.openSkyEntitiesMap.forEach((ent) => {
      const isThisSelected = (ent === targetEntity) || ((ent as any).openSkyData?.icao24 === flightData.icao24);
      if (ent.path) {
        ent.path.show = new Cesium.ConstantProperty(isThisSelected);
      }
    });

    this.createOrUpdateOverlay(viewer, flightData, targetEntity);
  }

  public deselectFlight(viewer?: Cesium.Viewer): void {
    this.selectedFlight = null;
    this.selectedEntity = null;
    this.isFullDetailsOpen = false;

    this.openSkyEntitiesMap.forEach((ent) => {
      if (ent.path) {
        ent.path.show = new Cesium.ConstantProperty(false);
      }
    });

    if (viewer) {
      this.removePopupOverlay(viewer);
    }
  }

  private setupPlaneClickListener(viewer: Cesium.Viewer): void {
    if (!viewer || !viewer.scene || !viewer.scene.canvas) return;

    if (!this.planeClickHandler || this.planeClickHandler.isDestroyed()) {
      this.planeClickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    } else {
      this.planeClickHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    this.planeClickHandler.setInputAction((click: any) => {
      if (!viewer) return;

      const pickedObjects = viewer.scene.drillPick(click.position);
      if (Array.isArray(pickedObjects) && pickedObjects.length > 0) {
        for (const picked of pickedObjects) {
          if (picked && picked.id && (picked.id as any).openSkyData) {
            const flightData: OpenSkyFlightInfo = (picked.id as any).openSkyData;
            this.selectFlight(viewer, flightData, picked.id, false);
            return;
          }
        }
      }

      this.deselectFlight(viewer);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  private removePlaneClickListener(): void {
    if (this.planeClickHandler && !this.planeClickHandler.isDestroyed()) {
      this.planeClickHandler.destroy();
      this.planeClickHandler = null;
    }
  }

  private createOrUpdateOverlay(viewer: Cesium.Viewer, flightInfo: OpenSkyFlightInfo, entity: Cesium.Entity): void {
    const container = viewer.container as HTMLElement;
    if (!container) return;

    let overlay = this.popupOverlayElement;
    if (!overlay || !overlay.parentElement) {
      overlay = document.createElement('div');

      const stopAllEvents = (e: Event) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      overlay.addEventListener('pointerdown', stopAllEvents);
      overlay.addEventListener('mousedown', stopAllEvents);
      overlay.addEventListener('mouseup', stopAllEvents);
      overlay.addEventListener('click', stopAllEvents);
      overlay.addEventListener('touchstart', stopAllEvents);

      container.style.position = 'relative';
      container.appendChild(overlay);
      this.popupOverlayElement = overlay;
    }

    if (this.isFullDetailsOpen) {
      overlay.className = 'flight-info-popup-card';
      overlay.innerHTML = `
        <div class="flight-popup-header">
          <div class="flight-popup-live">
            <span class="pulse-dot"></span>
            <span class="live-text">GLOBAL AIR RADAR</span>
          </div>
          <div class="flight-popup-header-actions">
            <span class="flight-num-badge">${flightInfo.callsign}</span>
            <button type="button" class="flight-popup-close-btn" id="flight-popup-close" title="Close Details">✕</button>
          </div>
        </div>
        <div class="flight-popup-body">
          <div class="flight-info-row">
            <span class="lbl">Flight Callsign:</span>
            <span class="val">${flightInfo.callsign}</span>
          </div>
          ${flightInfo.route ? `
          <div class="flight-info-row">
            <span class="lbl">Live Route:</span>
            <span class="val" style="color: #00ffcc; font-weight: bold;">${flightInfo.route}</span>
          </div>` : ''}
          ${flightInfo.aircraftModel ? `
          <div class="flight-info-row">
            <span class="lbl">Aircraft Model:</span>
            <span class="val">${flightInfo.aircraftModel}</span>
          </div>` : ''}
          ${flightInfo.registration ? `
          <div class="flight-info-row">
            <span class="lbl">Registration:</span>
            <span class="val">${flightInfo.registration}</span>
          </div>` : ''}
          <div class="flight-info-row">
            <span class="lbl">ICAO 24 Address:</span>
            <span class="val">${flightInfo.icao24.toUpperCase()}</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Origin / Operator:</span>
            <span class="val">${flightInfo.originCountry}</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Altitude:</span>
            <span class="val">${flightInfo.altitudeMeters}m (${flightInfo.altitudeFeet} ft)</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Ground Speed:</span>
            <span class="val">${flightInfo.velocityKnots} kts (${flightInfo.velocityKmh} km/h)</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Heading:</span>
            <span class="val">${flightInfo.headingDeg}°</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Vertical Rate:</span>
            <span class="val">${flightInfo.verticalRateMs} m/s</span>
          </div>
          <div class="flight-info-row">
            <span class="lbl">Status:</span>
            <span class="val">${flightInfo.onGround ? 'On Ground' : 'Airborne'}</span>
          </div>
        </div>
        <div class="flight-popup-pointer"></div>
      `;

      const closeBtn = overlay.querySelector('#flight-popup-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.isFullDetailsOpen = false;
          this.createOrUpdateOverlay(viewer, flightInfo, entity);
        });
      }
    } else {
      overlay.className = 'flight-tag-badge';
      overlay.innerHTML = `
        <div class="flight-tag-top">
          <span class="flight-tag-callsign"><span class="flight-tag-plane-icon">✈</span> ${flightInfo.callsign}</span>
          <button type="button" class="flight-tag-info-btn" id="flight-open-details-btn" title="View Full Flight Details">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </button>
        </div>
        <div class="flight-tag-bottom">
          ${flightInfo.route ? `<span style="color: #00ffcc; font-weight: 600;">${flightInfo.route}</span> • ` : ''}${flightInfo.originCountry} • ${flightInfo.altitudeMeters}m
        </div>
        <div class="flight-popup-pointer"></div>
      `;

      const infoBtn = overlay.querySelector('#flight-open-details-btn');
      if (infoBtn) {
        infoBtn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.isFullDetailsOpen = true;
          this.createOrUpdateOverlay(viewer, flightInfo, entity);
        });
      }
    }

    this.trackEntityPosition(viewer, entity);
  }

  private trackEntityPosition(viewer: Cesium.Viewer, entity: Cesium.Entity): void {
    if (!viewer || !viewer.scene) return;

    if (this.removePostRenderListener) {
      return;
    }

    const listener = viewer.scene.postRender.addEventListener(() => {
      if (!this.popupOverlayElement || !entity) return;

      const currentTime = viewer.clock.currentTime;
      const posProp = entity.position as any;
      const cartesianPos = posProp && typeof posProp.getValue === 'function' ? posProp.getValue(currentTime) : posProp;
      if (!cartesianPos) {
        this.popupOverlayElement.style.display = 'none';
        return;
      }

      if (viewer.scene.globe && viewer.scene.camera) {
        const occluder = new Cesium.Occluder(
          Cesium.BoundingSphere.fromEllipsoid(viewer.scene.globe.ellipsoid),
          viewer.scene.camera.position
        );
        if (!occluder.isPointVisible(cartesianPos)) {
          this.popupOverlayElement.style.display = 'none';
          return;
        }
      }

      const windowCoord = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPos);
      if (
        windowCoord &&
        windowCoord.x >= 0 &&
        windowCoord.y >= 0 &&
        windowCoord.x <= viewer.canvas.clientWidth &&
        windowCoord.y <= viewer.canvas.clientHeight
      ) {
        this.popupOverlayElement.style.display = 'block';
        const rect = this.popupOverlayElement.getBoundingClientRect();
        const left = Math.round(windowCoord.x - rect.width / 2);
        const top = Math.round(windowCoord.y - rect.height - 25);
        this.popupOverlayElement.style.transform = `translate3d(${left}px, ${top}px, 0px)`;
      } else {
        this.popupOverlayElement.style.display = 'none';
      }
    });

    this.removePostRenderListener = listener;
  }

  private removePopupOverlay(viewer?: Cesium.Viewer): void {
    if (this.removePostRenderListener) {
      this.removePostRenderListener();
      this.removePostRenderListener = null;
    }
    if (this.popupOverlayElement) {
      if (this.popupOverlayElement.parentElement) {
        this.popupOverlayElement.parentElement.removeChild(this.popupOverlayElement);
      }
      this.popupOverlayElement = null;
    }
  }
}
